import React, { useState, useEffect } from 'react';
import { firestore } from '../config/firebaseConfig';
import { collection, getDocs, deleteDoc, doc, updateDoc, setDoc, query, where } from 'firebase/firestore'; // Use setDoc para adicionar com ID customizado
import './ContatoList.css';
import ConfirmationModal from './ConfirmationModal';
import Carregando from './Carregando';
import { FcViewDetails } from "react-icons/fc";
import { FaClock, FaPauseCircle, FaCircle, FaUserGraduate } from 'react-icons/fa';
import { BsFillQuestionDiamondFill, BsFillExclamationCircleFill } from "react-icons/bs";

// Status mapping para ícones de status
const statusMapping = {
  "Pendente": { icon: <FaClock color="#d6ceaa" size="35" />, tooltip: "Status: Pendente - Aguardando convite" },
  "Convidado": { icon: <BsFillQuestionDiamondFill color="#d8523d" size="35" />, tooltip: "Status: Convidado - Aguardando resposta" },
  "Ativo": { icon: <FaCircle color="#2e8b57" size="35" />, tooltip: "Status: Ativo - Convite Aceito" },
  "Recusado": { icon: <BsFillExclamationCircleFill color="#5e2f46" size="35" />, tooltip: "Status: Convite Recusado" },
  "Suspenso": { icon: <FaPauseCircle color="#E27070" size="35" />, tooltip: "Status: Contato Suspenso" },
  "Bloqueado": { icon: <FaCircle color="#E27070" size="35" />, tooltip: "Status: Contato Bloqueado" }
};

// Componente para exibir o ícone de status
const StatusIcon = ({ status, onClick }) => {
  const { icon, tooltip } = statusMapping[status] || {};
  return <div className="icon-container" title={tooltip} onClick={onClick}>{icon}</div>;
};

// Função para gerar um ID alfanumérico aleatório
const generateDocId = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Componente para exibir uma linha da tabela
const ContatoRow = ({ contato, onShowDetails, onToggleStatus }) => {
  const { id, status, nome, tipoContato, origem, dataCriacao } = contato;

  return (
    <tr>
      <td>{nome}</td>
      <td>{tipoContato}</td>
      <td>
        <StatusIcon status={status} onClick={() => onToggleStatus(id, status)} />
      </td>
      <td>{origem}</td>
      <td>{new Date(dataCriacao).toLocaleDateString()}</td>
      <td>
        <div className="icon-container" onClick={() => onShowDetails(contato)}>
          <FcViewDetails size="45" />
        </div>
      </td>
    </tr>
  );
};

// Função para formatar telefone
const formatarTelefone = (telefone) => {
  if (!telefone) return ''; 
  const telefoneSemCodigo = telefone.replace(/^55/, ''); // Remove o código de país (se houver)
  return telefoneSemCodigo.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');    
};

// Função para formatar data
const formatarData = (dataNascimento) => {
  if (!dataNascimento) return ''; 
  const dateObj = new Date(dataNascimento);
  if (isNaN(dateObj.getTime())) return ''; // Data inválida
  const dia = String(dateObj.getUTCDate()).padStart(2, '0');
  const mes = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
  const ano = dateObj.getUTCFullYear();
  return `${dia}/${mes}/${ano}`; 
};

const ContatoList = ({ searchTerm }) => {
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedContato, setSelectedContato] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContato, setEditedContato] = useState(null);
  const [sortField, setSortField] = useState('dataCriacao');
  const [sortDirection, setSortDirection] = useState('desc');

  // Recuperar o tenantId do localStorage ou estado global
  const tenantId = localStorage.getItem('tenantId');

  useEffect(() => {
    if (tenantId) {
      fetchContatos();
    }
  }, [tenantId]);

  const fetchContatos = async () => {
    setLoading(true);
    try {
      const contatosCollection = collection(firestore, 'contatos');
      const querySnapshot = await getDocs(query(contatosCollection, where('tenantId', '==', tenantId)));
      const contatosList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setContatos(contatosList);
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (contatoId, currentStatus) => {
    if (currentStatus === 'Bloqueado' || currentStatus === 'Ativo') {
      const newStatus = currentStatus === 'Bloqueado' ? 'Ativo' : 'Bloqueado';
      try {
        await updateDoc(doc(firestore, 'contatos', contatoId), { status: newStatus });
        fetchContatos();
      } catch (error) {
        console.error('Erro ao atualizar status:', error);
      }
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(firestore, 'contatos', deleteId));
      fetchContatos();
      if (selectedContato && selectedContato.id === deleteId) {
        setShowDetails(false);
        setSelectedContato(null);
      }
    } catch (error) {
      console.error('Erro ao excluir contato:', error);
    } finally {
      setShowModal(false);
      setDeleteId(null);
    }
  };

  const handleShowDetails = (contato) => {
    setSelectedContato(contato);
    setEditedContato(contato);
    setIsEditing(false);
    setShowDetails(true);
  };

  const handleEdit = () => setIsEditing(true);

  const handleSave = async () => {
    try {
      const contatoRef = doc(firestore, 'contatos', editedContato.id);
      await updateDoc(contatoRef, {
        ...editedContato,
        tenantId: tenantId, // Certifique-se de manter o tenantId ao salvar as edições
        dataNascimento: new Date(editedContato.dataNascimento).toISOString().split('T')[0]
      });
      fetchContatos();
      setIsEditing(false);
      setShowDetails(false);
    } catch (error) {
      console.error('Erro ao salvar as alterações:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedContato((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSort = (field) => {
    setSortField(field);
    setSortDirection(prevDirection => prevDirection === 'asc' ? 'desc' : 'asc');
  };

  const sortedContatos = [...contatos].sort((a, b) => {
    let valueA = a[sortField];
    let valueB = b[sortField];

    if (sortField === 'dataCriacao' || sortField === 'dataNascimento') {
      return sortDirection === 'asc' ? new Date(valueA) - new Date(valueB) : new Date(valueB) - new Date(valueA);
    } else if (typeof valueA === 'string') {
      return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    } else if (typeof valueA === 'number') {
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    }
    return 0;
  });

  const filteredContatos = sortedContatos.filter(contato =>
    contato.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <Carregando />;

  return (
    <div className="contato-list-container">      
      <table className="contato-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('nome')}>Nome</th>
            <th onClick={() => handleSort('tipoContato')}>Tipo de Contato</th>
            <th onClick={() => handleSort('status')}>Status</th>
            <th onClick={() => handleSort('origem')}>Origem</th>
            <th onClick={() => handleSort('dataCriacao')}>Criado</th>
            <th>Detalhes</th>
          </tr>
        </thead>
        <tbody>
          {filteredContatos.length > 0 ? (
            filteredContatos.map(contato => (
              <ContatoRow key={contato.id} contato={contato} onShowDetails={handleShowDetails} onToggleStatus={toggleStatus} />
            ))
          ) : (
            <tr>
              <td colSpan="6">Nenhum registro encontrado.</td>
            </tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <ConfirmationModal onConfirm={handleDelete} onCancel={() => setShowModal(false)} />
      )}

      {showDetails && selectedContato && (
        <div className="details-modal">
          <h2>
            {isEditing ? (
              <input type="text" name="nome" value={editedContato.nome} onChange={handleChange} />
            ) : (
              <>
                {selectedContato.tipoContato === "Aluno" &&<FaUserGraduate style={{ marginRight: '8px', color: '#999' }} />} 
                {selectedContato.nome}
              </>
            )}
          </h2>
          <div className="action-buttons">
            {isEditing ? (
              <button onClick={handleSave}>Salvar</button>
            ) : (
              <button onClick={handleEdit}>Editar</button>
            )}
            <button onClick={() => {
              confirmDelete(selectedContato.id);
              setShowDetails(false);
            }}>Excluir</button>
            <button onClick={() => setShowDetails(false)}>Fechar</button>
          </div>
          <DetailsSection
            contato={selectedContato}
            isEditing={isEditing}
            editedContato={editedContato}
            handleChange={handleChange}
          />
        </div>
      )}
    </div>
  );
};

const DetailsSection = ({ contato, isEditing, editedContato, handleChange }) => (
  <>
    <p>
      <strong>Status:</strong> {isEditing ? (
        <select name="status" value={editedContato.status} onChange={handleChange}>
          <option value="Pendente">Pendente</option>
          <option value="Convidado">Convidado</option>
          <option value="Ativo">Ativo</option>
          <option value="Recusado">Recusado</option>
          <option value="Suspenso">Suspenso</option>
          <option value="Bloqueado">Bloqueado</option>
        </select>
      ) : (
        <StatusIcon status={contato.status} />
      )}
    </p>
    <p>
      <strong>Tipo de Contato:</strong> {isEditing ? (
        <select name="tipoContato" value={editedContato.tipoContato} onChange={handleChange}>
          <option value="Lead">Lead</option>
          <option value="Aluno">Aluno</option>
        </select>
      ) : (
        contato.tipoContato
      )}
    </p>
    <p><strong>Origem:</strong> {contato.origem}</p>
    <p>
      <strong>Data de Nascimento:</strong> {isEditing ? (
        <input type="date" name="dataNascimento" value={editedContato.dataNascimento.split('T')[0]} onChange={handleChange} />
      ) : (
        formatarData(contato.dataNascimento)
      )}
    </p>
    <p>
      <strong>Telefone:</strong> {isEditing ? (
        <input type="text" name="telefone" value={editedContato.telefone} onChange={handleChange} />
      ) : (
        formatarTelefone(contato.telefone)
      )}
    </p>
    <p>
      <strong>E-mail:</strong> {isEditing ? (
        <input type="email" name="email" value={editedContato.email} onChange={handleChange} />
      ) : (
        contato.email
      )}
    </p>
  </>
);

export default ContatoList;
